/* eslint-disable react-hooks/set-state-in-effect, react-hooks/immutability */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import usePersistedTab from '../../hooks/usePersistedTab';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { Building2, Users, FileText, CreditCard, Activity, Flame, MessageCircle, FileDown, Database, RefreshCw, CheckCircle2, Network } from 'lucide-react';
import { Button } from '../../components/ui/button';
import SuperAdminUsersTab from './superadmin/SuperAdminUsersTab';
import SuperAdminProspectsTab from './superadmin/SuperAdminProspectsTab';
import SuperAdminSellerVoiceTab from './superadmin/SuperAdminSellerVoiceTab';
import SuperAdminGroupsTab from './superadmin/SuperAdminGroupsTab';
import {
    SuperAdminAgenciesTab,
    SuperAdminMandatesTab,
    SuperAdminSubscriptionsTab,
    SuperAdminLogsTab,
} from './superadmin/SuperAdminTables';
import SuperAdminLoginForm from './superadmin/SuperAdminLoginForm';
import SuperAdminHeader from './superadmin/SuperAdminHeader';
import SuperAdminStatsGrid from './superadmin/SuperAdminStatsGrid';
import { ResetPasswordDialog, UserDetailDialog } from './superadmin/SuperAdminDialogs';

const API_URL = '';

function SuperAdminPage() {
    const navigate = useNavigate();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loginLoading, setLoginLoading] = useState(false);

    // Login form
    const [loginData, setLoginData] = useState({ email: '', password: '' });

    // Data states
    const [stats, setStats] = useState(null);
    const [agencies, setAgencies] = useState([]);
    const [users, setUsers] = useState([]);
    const [mandates, setMandates] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [auditLogs, setAuditLogs] = useState([]);

    // Search
    const [searchTerm, setSearchTerm] = useState('');

    // Dialogs
    const [resetPasswordDialog, setResetPasswordDialog] = useState({ open: false, user: null });
    const [newPassword, setNewPassword] = useState('');
    const [userDetailDialog, setUserDetailDialog] = useState({ open: false, user: null });

    // Réparation des données démo (prod)
    const [demoRepairLoading, setDemoRepairLoading] = useState(false);
    const [demoState, setDemoState] = useState(null);
    const [activeTab, setActiveTab] = usePersistedTab(
        'homly_superadmin_tab',
        'users',
        ['users', 'agencies', 'groups', 'mandates', 'subscriptions', 'logs', 'prospects', 'seller-voice'],
    );

    useEffect(() => {
        const savedToken = localStorage.getItem('superadmin_token');
        if (savedToken) {
            setToken(savedToken);
            setIsLoggedIn(true);
        }
    }, []);

    useEffect(() => {
        if (isLoggedIn && token) {
            loadAllData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, token]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginLoading(true);
        try {
            const response = await axios.post(`${API_URL}/api/super-admin/login`, loginData);
            const { access_token } = response.data;
            localStorage.setItem('superadmin_token', access_token);
            setToken(access_token);
            setIsLoggedIn(true);
            toast.success('Connexion réussie');
        } catch (err) {
            toast.error('Identifiants incorrects');
        } finally {
            setLoginLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('superadmin_token');
        setToken(null);
        setIsLoggedIn(false);
        setStats(null);
        setAgencies([]);
        setUsers([]);
        setMandates([]);
    };

    const handleDownloadPlaquette = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/super-admin/plaquette`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            if (window.self !== window.top) {
                window.open(url, '_blank', 'noopener');
                toast.success('Plaquette ouverte dans un nouvel onglet');
            } else {
                const a = document.createElement('a');
                a.href = url;
                a.download = 'Homly-Plaquette-Commerciale.pdf';
                document.body.appendChild(a);
                a.click();
                a.remove();
                toast.success('Plaquette téléchargée');
            }
            setTimeout(() => window.URL.revokeObjectURL(url), 4000);
        } catch (e) {
            toast.error('Erreur lors du téléchargement de la plaquette');
        }
    };

    const checkDemoState = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/admin/repair-demo/preview`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setDemoState(res.data);
            toast.success(`État vérifié · ${res.data.canonical_active_mandates} mandats actifs`);
        } catch (e) {
            toast.error('Erreur lors de la vérification de l\'état démo');
        }
    };

    const repairDemoData = async () => {
        if (!window.confirm('Repeupler les données de démonstration (agence Immo Excellence) sur CETTE base ?\n\nIdempotent et sans risque : crée/complète les mandats, avis, messages IA, visites et offres de la démo. N\'affecte pas vos vraies agences.')) {
            return;
        }
        setDemoRepairLoading(true);
        try {
            const res = await axios.post(
                `${API_URL}/api/admin/repair-demo?nuke_legacy=false`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const fs = res.data?.report?.final_state || {};
            setDemoState({
                canonical_agency_exists: true,
                canonical_active_mandates: fs.active_mandates,
            });
            toast.success(`Démo repeuplée · ${fs.active_mandates} mandats actifs, ${fs.users_count} utilisateurs`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la réparation des données démo');
        } finally {
            setDemoRepairLoading(false);
        }
    };

    const loadAllData = async () => {
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const [statsRes, agenciesRes, usersRes, mandatesRes, subsRes, logsRes] = await Promise.all([
                axios.get(`${API_URL}/api/super-admin/stats`, { headers }),
                axios.get(`${API_URL}/api/super-admin/agencies`, { headers }),
                axios.get(`${API_URL}/api/super-admin/users`, { headers }),
                axios.get(`${API_URL}/api/super-admin/mandates`, { headers }),
                axios.get(`${API_URL}/api/super-admin/subscriptions`, { headers }),
                axios.get(`${API_URL}/api/super-admin/audit-logs?limit=50`, { headers })
            ]);
            setStats(statsRes.data);
            setAgencies(agenciesRes.data);
            setUsers(usersRes.data);
            setMandates(mandatesRes.data);
            setSubscriptions(subsRes.data);
            setAuditLogs(logsRes.data);
        } catch (err) {
            toast.error('Erreur lors du chargement');
            if (err.response?.status === 403) {
                handleLogout();
            }
        } finally {
            setLoading(false);
        }
    };

    const resetPassword = async () => {
        if (!newPassword || newPassword.length < 8) {
            toast.error('Le mot de passe doit contenir au moins 8 caractères');
            return;
        }
        try {
            const headers = { Authorization: `Bearer ${token}` };
            await axios.post(
                `${API_URL}/api/super-admin/users/${resetPasswordDialog.user.id}/reset-password`,
                { user_id: resetPasswordDialog.user.id, new_password: newPassword },
                { headers }
            );
            toast.success(`Mot de passe réinitialisé pour ${resetPasswordDialog.user.email}`);
            setResetPasswordDialog({ open: false, user: null });
            setNewPassword('');
            loadAllData();
        } catch (err) {
            toast.error('Erreur lors de la réinitialisation');
        }
    };

    const toggleUserStatus = async (userId) => {
        try {
            const headers = { Authorization: `Bearer ${token}` };
            await axios.post(`${API_URL}/api/super-admin/users/${userId}/toggle-status`, {}, { headers });
            toast.success('Statut modifié');
            loadAllData();
        } catch (err) {
            toast.error('Erreur');
        }
    };

    const viewUserDetail = async (userId) => {
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const response = await axios.get(`${API_URL}/api/super-admin/users/${userId}`, { headers });
            setUserDetailDialog({ open: true, user: response.data });
        } catch (err) {
            toast.error('Erreur');
        }
    };

    // Login page
    if (!isLoggedIn) {
        return (
            <SuperAdminLoginForm
                loginData={loginData}
                onChange={setLoginData}
                onSubmit={handleLogin}
                loading={loginLoading}
            />
        );
    }

    // Loading
    if (loading && !stats) {
        return (
            <div className="min-h-screen bg-background p-6">
                <div className="max-w-7xl mx-auto space-y-6">
                    <Skeleton className="h-12 w-64 bg-gray-800" />
                    <div className="grid grid-cols-4 gap-4">
                        {[...Array(4)].map((_, i) => (
                            <Skeleton key={`stat-sk-${i}`} className="h-32 bg-gray-800" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background text-white" data-testid="superadmin-panel">
            <SuperAdminHeader onLogout={handleLogout} />

            <main className="max-w-7xl mx-auto p-6 space-y-6">
                <SuperAdminStatsGrid stats={stats} />

                {/* Plaquette commerciale — réservée aux commerciaux Homly */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-border bg-gray-800/50 p-4" data-testid="plaquette-card">
                    <div className="flex items-center gap-3">
                        <FileDown className="w-5 h-5 text-primary" />
                        <div>
                            <p className="font-semibold text-white">Plaquette commerciale</p>
                            <p className="text-sm text-muted-foreground">Document de vente premium (PDF) · réservé aux commerciaux</p>
                        </div>
                    </div>
                    <Button onClick={handleDownloadPlaquette} className="bg-primary hover:bg-primary/90" data-testid="download-plaquette-button">
                        <FileDown className="w-4 h-4 mr-2" />
                        Télécharger la plaquette
                    </Button>
                </div>

                {/* Données démo — repeuplement après (re)déploiement */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-border bg-gray-800/50 p-4" data-testid="demo-repair-card">
                    <div className="flex items-center gap-3">
                        <Database className="w-5 h-5 text-primary" />
                        <div>
                            <p className="font-semibold text-white">Données de démonstration</p>
                            <p className="text-sm text-muted-foreground">
                                {"Repeuple l'agence de démo (mandats, avis, messages IA, visites, offres) — utile après un redéploiement. "}
                                {demoState && (
                                    <span className="ml-1 inline-flex items-center gap-1 text-green-400">
                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                        {demoState.canonical_active_mandates} mandats actifs
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={checkDemoState} className="border-border" data-testid="check-demo-state-button">
                            {"Vérifier l'état"}
                        </Button>
                        <Button onClick={repairDemoData} disabled={demoRepairLoading} className="bg-primary hover:bg-primary/90" data-testid="repair-demo-button">
                            <RefreshCw className={`w-4 h-4 mr-2 ${demoRepairLoading ? 'animate-spin' : ''}`} />
                            {demoRepairLoading ? 'Repeuplement…' : 'Repeupler la démo'}
                        </Button>
                    </div>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                    <TabsList className="bg-gray-800 border border-border">
                        <TabsTrigger value="users" className="data-[state=active]:bg-primary">
                            <Users className="w-4 h-4 mr-2" />
                            Utilisateurs
                        </TabsTrigger>
                        <TabsTrigger value="agencies" className="data-[state=active]:bg-primary">
                            <Building2 className="w-4 h-4 mr-2" />
                            Agences
                        </TabsTrigger>
                        <TabsTrigger value="groups" className="data-[state=active]:bg-primary" data-testid="tab-groups">
                            <Network className="w-4 h-4 mr-2" />
                            Groupes
                        </TabsTrigger>
                        <TabsTrigger value="mandates" className="data-[state=active]:bg-primary">
                            <FileText className="w-4 h-4 mr-2" />
                            Mandats
                        </TabsTrigger>
                        <TabsTrigger value="subscriptions" className="data-[state=active]:bg-primary">
                            <CreditCard className="w-4 h-4 mr-2" />
                            Abonnements
                        </TabsTrigger>
                        <TabsTrigger value="logs" className="data-[state=active]:bg-primary">
                            <Activity className="w-4 h-4 mr-2" />
                            Activité
                        </TabsTrigger>
                        <TabsTrigger value="prospects" className="data-[state=active]:bg-primary" data-testid="tab-prospects">
                            <Flame className="w-4 h-4 mr-2" />
                            Prospects
                        </TabsTrigger>
                        <TabsTrigger value="seller-voice" className="data-[state=active]:bg-primary" data-testid="tab-seller-voice">
                            <MessageCircle className="w-4 h-4 mr-2" />
                            Voix vendeur
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="users">
                        <SuperAdminUsersTab
                            users={users}
                            searchTerm={searchTerm}
                            onSearchChange={setSearchTerm}
                            onViewUser={viewUserDetail}
                            onOpenResetPasswordDialog={(user) => setResetPasswordDialog({ open: true, user })}
                            onToggleStatus={toggleUserStatus}
                        />
                    </TabsContent>

                    <TabsContent value="agencies">
                        <SuperAdminAgenciesTab agencies={agencies} />
                    </TabsContent>

                    <TabsContent value="groups">
                        <SuperAdminGroupsTab token={token} />
                    </TabsContent>

                    <TabsContent value="mandates">
                        <SuperAdminMandatesTab mandates={mandates} />
                    </TabsContent>

                    <TabsContent value="subscriptions">
                        <SuperAdminSubscriptionsTab subscriptions={subscriptions} />
                    </TabsContent>

                    <TabsContent value="logs">
                        <SuperAdminLogsTab auditLogs={auditLogs} />
                    </TabsContent>

                    <TabsContent value="prospects">
                        <SuperAdminProspectsTab token={token} />
                    </TabsContent>

                    <TabsContent value="seller-voice">
                        <SuperAdminSellerVoiceTab token={token} />
                    </TabsContent>
                </Tabs>
            </main>

            <ResetPasswordDialog
                dialog={resetPasswordDialog}
                onChange={setResetPasswordDialog}
                newPassword={newPassword}
                onNewPasswordChange={setNewPassword}
                onConfirm={resetPassword}
            />

            <UserDetailDialog
                dialog={userDetailDialog}
                onChange={setUserDetailDialog}
            />
        </div>
    );
}

export default SuperAdminPage;
